import React from 'react';
import { X, Check } from 'lucide-react';
import type { WorkSummary } from '../../../services/postService';

interface WorkSelectorModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (works: WorkSummary[]) => void;
    works: WorkSummary[];
    isLoading?: boolean;
    selectedWorkIds?: Array<string | number>;
    zIndex?: number;
}

const WorkSelectorModal: React.FC<WorkSelectorModalProps> = ({
    isOpen,
    onClose,
    onSelect,
    works,
    isLoading = false,
    selectedWorkIds = [],
    zIndex = 60
}) => {
    const [selectedIds, setSelectedIds] = React.useState<Array<string | number>>([]);

    React.useEffect(() => {
        if (!isOpen) {
            setSelectedIds([]);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleToggleSelect = (id: string | number) => {
        if (selectedWorkIds.includes(id)) return; // Cannot toggle already added works

        setSelectedIds(prev =>
            prev.includes(id)
                ? prev.filter(wId => wId !== id)
                : [...prev, id]
        );
    };

    const handleConfirm = () => {
        const selectedWorks = works.filter(w => selectedIds.includes(w.id));
        onSelect(selectedWorks);
        onClose();
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex }}>
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[80vh] animate-scale-in">
                <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-900">选择示例作品</h3>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {isLoading ? (
                        <div className="py-16 flex flex-col items-center justify-center gap-3 text-sm text-gray-500">
                            <div className="w-8 h-8 rounded-full border-4 border-gray-200 border-t-black animate-spin" />
                            <div>正在加载作品...</div>
                        </div>
                    ) : works.length === 0 ? (
                        <div className="py-16 text-center text-sm text-gray-400">
                            暂无可选作品
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                            {works.map((work) => {
                                const isAdded = selectedWorkIds.includes(work.id);
                                const isSelected = selectedIds.includes(work.id);

                                return (
                                    <div
                                        key={work.id}
                                        onClick={() => handleToggleSelect(work.id)}
                                        className={`
                                            relative rounded-xl overflow-hidden cursor-pointer group aspect-video border-2 transition-all
                                            ${isAdded ? 'opacity-50 cursor-not-allowed border-gray-100' : ''}
                                            ${isSelected ? 'border-black ring-2 ring-black/10' : 'border-transparent hover:border-gray-200'}
                                        `}
                                    >
                                        <img
                                            src={work.thumbnailUrl}
                                            alt={work.title}
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                                            <p className="text-white text-xs font-medium truncate">{work.title}</p>
                                        </div>

                                        {(isSelected || isAdded) && (
                                            <div className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center ${isAdded ? 'bg-gray-400' : 'bg-black'} text-white`}>
                                                <Check className="w-3.5 h-3.5" />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                    >
                        取消
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={isLoading || selectedIds.length === 0}
                        className="px-6 py-2 bg-black text-white text-sm font-bold rounded-full hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        确认添加 ({selectedIds.length})
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WorkSelectorModal;
